import { useState, useEffect, useRef, useCallback } from 'react'

export interface UseVoiceInputReturn {
  isListening: boolean
  isSupported: boolean
  transcript: string
  error: string | null
  startListening: () => void
  stopListening: () => void
  resetTranscript: () => void
}

const ERROR_MESSAGES: Record<string, string> = {
  'not-allowed': 'Microphone access denied. Please allow microphone access in your browser settings.',
  'no-speech': 'No speech detected. Please try again.',
  'audio-capture': 'No microphone found. Please connect a microphone.',
  'network': 'Network error. Please check your connection.',
  'aborted': '', // User-initiated stop — not an error
  'service-not-allowed': 'Speech recognition service not available.',
}

function getSpeechRecognitionConstructor(): (typeof SpeechRecognition) | null {
  if (typeof window === 'undefined') return null
  return window.SpeechRecognition || window.webkitSpeechRecognition || null
}

export function useVoiceInput(): UseVoiceInputReturn {
  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const finalTranscriptRef = useRef('')

  const isSupported = !!getSpeechRecognitionConstructor()

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort()
        recognitionRef.current = null
      }
    }
  }, [])

  const startListening = useCallback(() => {
    const SpeechRecognitionCtor = getSpeechRecognitionConstructor()
    if (!SpeechRecognitionCtor) return

    // Clean up any existing instance
    if (recognitionRef.current) {
      recognitionRef.current.abort()
    }

    setError(null)
    setTranscript('')
    finalTranscriptRef.current = ''

    const recognition = new SpeechRecognitionCtor()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onstart = () => {
      setIsListening(true)
    }

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = ''
      let finalPortion = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          finalPortion += result[0].transcript
        } else {
          interimTranscript += result[0].transcript
        }
      }

      if (finalPortion) {
        finalTranscriptRef.current += finalPortion
      }

      const fullTranscript = (finalTranscriptRef.current + ' ' + interimTranscript).trim()
      setTranscript(fullTranscript)
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      const message = ERROR_MESSAGES[event.error] || `Voice input error: ${event.error}`
      if (message) {
        setError(message)
      }
      setIsListening(false)
    }

    recognition.onend = () => {
      setIsListening(false)
      recognitionRef.current = null
    }

    recognitionRef.current = recognition

    try {
      recognition.start()
    } catch {
      setError('Failed to start voice input. Please try again.')
      setIsListening(false)
    }
  }, [])

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop()
    }
  }, [])

  const resetTranscript = useCallback(() => {
    setTranscript('')
    finalTranscriptRef.current = ''
    setError(null)
  }, [])

  return {
    isListening,
    isSupported,
    transcript,
    error,
    startListening,
    stopListening,
    resetTranscript,
  }
}
